/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {FleksupService} from '../../../../app/services/FleksupService'
import moment from 'moment'

type Props = {
  className?: string
}

const ChartsWidget6: React.FC<Props> = ({className}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const [sumValue, setSumValue] = useState(0)
  const [filters, setFilters] = useState({
    timePeriod: 'weekly',
  })
  useEffect(() => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))
    let chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, {
        data: [],
        categories: [],
        maxValue: 0,
        minValue: 0,
      })
    )
    FleksupService.getDashboardSalesReports(filters.timePeriod).then((res) => {
      const values = []
      const dates = []
      const startOfWeek = moment().startOf('isoWeek')
      const startOfMonth = moment().startOf('month')

      if(filters.timePeriod === "weekly"){
        for(let i=0;i<7;i++){
          const dateValue = res.data.data.find((item:any) => startOfWeek.format('DD.MM.YYYY') === moment(item.created_date).format('DD.MM.YYYY'))?.amount
          values.push(dateValue ?? 0)
          dates.push(startOfWeek.format('DD'))
          startOfWeek.add(1, 'days')
        }
      }
      else if(filters.timePeriod === 'monthly'){
        for(let i=0;i<startOfMonth.daysInMonth();i++){
          const dateValue = res.data.data.find((item:any) => startOfMonth.format('DD.MM.YYYY') === moment(item.created_date).format('DD.MM.YYYY'))?.amount
          values.push(dateValue ?? 0)
          dates.push(startOfMonth.format('DD'))
          startOfMonth.add(1, 'days')
        }
      }

      setSumValue(values.reduce((prevValue: any, current: any) => prevValue + current, 0))
      chart = new ApexCharts(
        chartRef.current,
        getChartOptions(height, {
          data: values,
          categories:  dates,
          maxValue: Math.max(...values),
          minValue: 0,
          tickAmount: values.length,
        })
      )
      if (chart) {
        chart.render()
      }
    })

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, filters])

  return (
    <div className='card card-stretch'>
      <div className='card-header py-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder text-dark'>Satış Grafiği</span>
        </h3>
        <div className='card-toolbar'>
          <button
            type='button'
            className={`btn btn-${filters.timePeriod === 'weekly' ? 'primary' : 'light'}`}
            onClick={() => {
              setFilters((prevState) => ({
                ...prevState,
                timePeriod: 'weekly',
              }))
            }}
          >
            Haftalık
          </button>

          <button
            type='button'
            className={`btn ms-2 btn-${filters.timePeriod === 'monthly' ? 'primary' : 'light'}`}
            onClick={() => {
              setFilters((prevState) => ({
                ...prevState,
                timePeriod: 'monthly',
              }))
            }}
          >
            Aylık
          </button>
        </div>
      </div>

      <div className='card-body d-flex justify-content-between flex-column'>
        <div className='px-9 mb-5'>
          <div className='d-flex align-items-center mb-2'>
            <span className='fs-2hx fw-bolder text-gray-800 me-2 lh-1'>{ new Intl.NumberFormat('tr-TR', {
              style: 'currency',
              currency: 'TRY',
              minimumFractionDigits: 2,
            }).format(sumValue ?? 0).replace('₺', '')+' ₺'}</span>
          </div>
          <span className='fs-6 fw-bold text-gray-400'>
            Bu {filters.timePeriod === 'weekly' ? 'haftaki' : 'aylık'} toplam satış tutarı
          </span>
        </div>
        <div
          ref={chartRef}
          id='kt_charts_widget_4'
          className='min-h-auto ps-4 pe-6'
          style={{
            height: '400px',
          }}
        />
      </div>
    </div>
  )
}

export {ChartsWidget6}

function getChartOptions(height: number, chartOptions: any): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-border-dashed-color')

  const baseColor = getCSSVariableValue('--bs-primary')
  const lightColor = getCSSVariableValue('--bs-primary')

  return {
    series: [
      {
        name: 'Toplam Satış',
        data: chartOptions.data,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [0, 80, 100],
      },
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: chartOptions.categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tickAmount: chartOptions.tickAmount,
      labels: {
        rotate: 0,
        rotateAlways: true,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      max: chartOptions.maxValue,
      min: 0,
      tickAmount: 6,
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (val) {
          return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 2,
          }).format(val ?? 0).replace('₺', '')+' ₺'
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 2,
          }).format(val ?? 0).replace('₺', '')+' ₺'
        },
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: [baseColor],
      strokeWidth: 3,
    },
  }
}
